import { cn } from '@/lib/utils';

/*
 * A status pill.
 *
 * **It takes a display label, never a slug.** That is defect P5: the
 * prototype's admin and employee job lists render `window_cleaning` and
 * `deep_cleaning` straight from the database, while its own client portal
 * renders "Deep Cleaning" correctly. The mapping already existed and two
 * surfaces did not use it.
 *
 * So `label` is what a person reads and `tone` is what it looks like. There is
 * no code path that turns a database value into a badge, which is what makes
 * the defect unrepeatable rather than merely fixed — `<StatusBadge label={job.status} />`
 * is a type error, because `status` is a slug union and `label` is a string
 * the caller has to have looked up.
 *
 * The colours come from `--status-*-text`, not `--status-*`. A badge is 12px,
 * which WCAG counts as body text at 4.5:1, and four of the six fill colours are
 * under 3:1 on white. See design-system.md §2.
 */

export type StatusTone =
  'scheduled' | 'in-progress' | 'completed' | 'priority' | 'cancelled' | 'pending';

const TONE_CLASSES: Record<StatusTone, string> = {
  scheduled: 'bg-status-scheduled/15 text-status-scheduled-text',
  'in-progress': 'bg-status-in-progress/15 text-status-in-progress-text',
  completed: 'bg-status-completed/15 text-status-completed-text',
  priority: 'bg-status-priority/15 text-status-priority-text',
  cancelled: 'bg-status-cancelled/15 text-status-cancelled-text',
  pending: 'bg-status-pending/15 text-status-pending-text',
};

export interface StatusBadgeProps {
  /**
   * What a person reads. "Deep Cleaning", "In Progress", "Awaiting Approval".
   *
   * Never `job.status` or `serviceType.slug` — look up the display value first.
   * If there is no display value for something, that is the bug to fix.
   */
  label: string;
  tone: StatusTone;
  className?: string;
}

export function StatusBadge({ label, tone, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-meta font-medium',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}
